import {
  Anchor,
  AspectRatio,
  BackgroundImage,
  Box,
  Button,
  Container,
  Group,
  Overlay,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import { useVendorAvailability } from '@services/availabilitytime/hooks/useVendorAvailability';
import { IconBrandInstagram, IconChevronLeft } from '@tabler/icons-react';
import storageUtil from '@utils/storageUtil';
import Image from 'next/image';
import React from 'react';
import UserButton from '../../auth/components/userButton';
import VendorInfoModal from '../modals/vendorInfoModal';
import { HomePageProps } from '../types';

interface VendorMenuProps {
  vendor: HomePageProps['vendor'];
}

const VendorHeader: React.FC<VendorMenuProps> = ({ vendor }) => {
  const theme = useMantineTheme();
  const [opened, handler] = useDisclosure();

  const { width, ref } = useElementSize();

  const logoUrl =
    vendor?.subDomain && vendor?.logo
      ? storageUtil.getItemImage(vendor.subDomain, vendor.logo)
      : storageUtil.getCoverImage('ss-icon.svg');

  const coverUrl =
    vendor?.subDomain && vendor?.cover
      ? storageUtil.getItemImage(vendor.subDomain, vendor.cover)
      : storageUtil.getCoverImage('cover.jpg');

  useVendorAvailability({
    enabled: !!vendor?.id,
  });

  return (
    <>
      <BackgroundImage src={coverUrl} pos="relative" h={128}>
        <Overlay
          zIndex={0}
          gradient="linear-gradient(145deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0) 100%)"
          opacity={0.85}
        />
        <Container size="lg" ref={ref}>
          <Group justify="flex-end">
            <Box>
              <UserButton />
            </Box>
          </Group>
          <Box style={{ position: 'absolute', bottom: '-42px', width }}>
            <Group justify="space-between" align="flex-end">
              <Paper radius="xl" shadow="sm" withBorder>
                <AspectRatio w={84} ratio={1 / 1} mx="auto">
                  <Image
                    alt={vendor?.name || ''}
                    width={84}
                    height={84}
                    src={logoUrl}
                    style={{ borderRadius: theme.radius.md }}
                  />
                </AspectRatio>
              </Paper>

              <Group gap="xs">
                <Button
                  id="click_more_info"
                  rightSection={<IconChevronLeft size={23} />}
                  variant="subtle"
                  onClick={handler.open}
                  size="compact-sm"
                >
                  اطلاعات بیشتر
                </Button>
              </Group>
            </Group>
          </Box>
        </Container>
      </BackgroundImage>

      <Container size="lg" pt="lg" mt={40}>
        <Stack gap="xs">
          <Title order={1} size="h5">
            {vendor?.name}
          </Title>

          <Stack gap="sm">
            <Text size="sm">{vendor?.description}</Text>
          </Stack>

          <Group>
            <Anchor size="sm" color="blue.9" href={`tel:${vendor?.phoneNumber}`}>
              {vendor?.phoneNumber}
            </Anchor>
          </Group>

          <Group>
            {vendor?.instagramUserName ? (
              <Anchor
                size="sm"
                color="blue.9"
                target="_blank"
                href={`https://www.instagram.com/${vendor?.instagramUserName}`}
              >
                <Group gap={2}>
                  <IconBrandInstagram size="1.1rem" />
                  {vendor?.instagramUserName}
                </Group>
              </Anchor>
            ) : null}
          </Group>
        </Stack>
      </Container>

      <VendorInfoModal vendor={vendor} opened={opened} onClose={handler.close} />
    </>
  );
};

export default VendorHeader;
